/**
 * Sandbox mode - in-memory demo data used ONLY when the backend is
 * unreachable (network error, not an HTTP error). Lets every page and the
 * full signup → browse → book → dashboard workflow run locally with no
 * server, no stored credentials and no network calls. Nothing persists:
 * state lives in module memory and dies on refresh.
 */

import { SANDBOX_ENABLED } from './apiClient';

let seq = 0;
const sid = (p) => `sb_${p}_${++seq}`;

// Vendor ids are stable slugs (not random) so deep links like
// /vendor/sb-taco-norte survive a full page reload.
let vSeq = 0;
const V = (name, category, scene, theme, tagline, priceFrom, priceUnit, city, badge) => {
  vSeq += 1;
  return {
    id: `sb-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`, name, category, scene, theme, tagline,
    description: `${name} - a demo ${category.replace('-', ' ')} vendor shown while VGO runs in sandbox mode. Real vendors appear when the app is connected to the VGO backend.`,
    rating: 4.6 + (vSeq % 4) * 0.1, reviewsCount: 40 + vSeq * 7,
    priceFrom, priceUnit, badge, city,
    serviceArea: `${city} + 25 miles`, yearsInBusiness: 1 + (vSeq % 6),
    eventTypes: ['Corporate', 'Wedding', 'Birthday', 'Festival', 'Private'],
    included: ['Setup and teardown', 'Travel within service area'],
    coverPhoto: null, photos: [], isLive: vSeq % 3 === 0,
    providerType: category === 'catering' ? 'catering' : category === 'rentals' ? 'rental' : category === 'entertainment' ? 'entertainment' : 'vendor',
    offersCatering: false, menuItems: [], owner: null, sandbox: true,
  };
};

export const SANDBOX_VENDORS = [
  V('Taco Norte', 'food-trucks', 'truck', 0, 'Mexican · Tacos, birria', 450, 'event', 'San Diego', 'Top rated'),
  V('Seoul Wheels', 'food-trucks', 'truck', 6, 'Korean fusion · Bulgogi bowls', 520, 'event', 'San Diego'),
  V('Smash Bros Burgers', 'food-trucks', 'truck', 3, 'American · Smash burgers', 480, 'event', 'Chula Vista', 'Popular'),
  V('Napoli on Wheels', 'food-trucks', 'truck', 1, 'Italian · Wood-fired pizza', 600, 'event', 'La Jolla', 'New'),
  V('Casa Verde Catering', 'catering', 'platter', 1, 'Mediterranean · Full service', 28, 'guest', 'San Diego', 'Top rated'),
  V('Golden Fork Events', 'catering', 'platter', 3, 'American classics · Buffet', 24, 'guest', 'Oceanside'),
  V('The Grazing Board', 'catering', 'platter', 5, 'Charcuterie & grazing tables', 19, 'guest', 'San Diego', 'New'),
  V('The Tipsy Trailer', 'bars', 'bar', 2, 'Cocktail bar · Licensed', 650, 'event', 'San Diego', 'Popular'),
  V('Brew & Co. Espresso', 'bars', 'coffee', 7, 'Coffee cart · Espresso & matcha', 380, 'event', 'Encinitas'),
  V('Agave Social', 'bars', 'bar', 0, 'Margarita & mezcal bar', 700, 'event', 'San Diego', 'Top rated'),
  V('BigTop Tents', 'rentals', 'tent', 0, 'Tents 10×10 to 40×80', 220, 'day', 'San Diego', 'Popular'),
  V('Jump City Inflatables', 'rentals', 'bounce', 6, 'Bounce houses & slides', 180, 'day', 'El Cajon', 'Top rated'),
  V('Coastal Canopy Co.', 'rentals', 'tent', 1, 'Sailcloth & frame tents', 340, 'day', 'Del Mar'),
  V('DJ Marisol', 'entertainment', 'dj', 2, 'Open-format DJ · Lights & PA', 550, 'event', 'San Diego', 'Top rated'),
  V('Flashbox Photo Co.', 'entertainment', 'photobooth', 4, '360° & print booths', 400, 'event', 'San Diego'),
  V('Sugar Rush Desserts', 'entertainment', 'dessert', 0, 'Cake & dessert tables', 260, 'event', 'Carlsbad', 'New'),
];

// Catering vendors also demo the scheduled-pickup order flow; The Grazing
// Board doubles as the home-kitchen example.
const CATERING_PICKUP = {
  'sb-casa-verde-catering': {
    pickup: { address: '2210 Adams Ave, San Diego', instructions: 'Ring the side bell', prepTimeMinutes: 90, advanceNoticeHours: 24 },
    menuItems: [
      { id: 'cv-1', name: 'Mezze platter (serves 10)', price: 95, description: 'Hummus, baba ganoush, dolmas, warm pita' },
      { id: 'cv-2', name: 'Chicken shawarma tray (serves 10)', price: 140 },
      { id: 'cv-3', name: 'Falafel wrap box (12)', price: 84 },
    ],
  },
  'sb-the-grazing-board': {
    homeKitchen: true,
    pickup: { address: 'North Park (exact address after ordering)', prepTimeMinutes: 120, advanceNoticeHours: 48 },
    menuItems: [
      { id: 'gb-1', name: 'Grazing box - small', price: 45, description: 'Cheese, charcuterie, fruit, crackers' },
      { id: 'gb-2', name: 'Grazing table (serves 25)', price: 425 },
    ],
  },
};
SANDBOX_VENDORS.forEach((v) => Object.assign(v, CATERING_PICKUP[v.id]));

export function sandboxListing(vendor) {
  return {
    id: `${vendor.id}-main`,
    vendorId: vendor.id,
    title: `${vendor.name} - standard booking`,
    description: vendor.description,
    price: vendor.priceFrom,
    unit: vendor.priceUnit,
    depositPct: 25,
    minBooking: vendor.priceUnit === 'guest' ? vendor.priceFrom * 25 : vendor.priceFrom,
    minimumHours: 2,
    addOns: [],
  };
}

export function sandboxReviews(vendorId) {
  return [
    { id: sid('r'), vendorId, author: 'Jordan M.', rating: 5, date: new Date(Date.now() - 12 * 864e5).toISOString(), text: 'Booked for a company offsite - effortless from request to payout. (Sandbox demo review.)' },
    { id: sid('r'), vendorId, author: 'Priya S.', rating: 4.5, date: new Date(Date.now() - 40 * 864e5).toISOString(), text: 'Great communication and on time. Would book again. (Sandbox demo review.)' },
  ];
}

// Demo menus so the preorder flow is exercisable without a backend.
const SANDBOX_MENUS = {
  'sb-taco-norte': [
    { id: 'm1', name: 'Birria Tacos (3)', description: 'Slow-braised beef, consommé', price: 14 },
    { id: 'm2', name: 'Quesabirria', description: 'Griddled, cheesy', price: 12 },
    { id: 'm3', name: 'Horchata', description: 'House-made', price: 5 },
  ],
  'sb-seoul-wheels': [
    { id: 'm1', name: 'Bulgogi Bowl', description: 'Marinated beef, rice, veg', price: 15 },
    { id: 'm2', name: 'Kimchi Fries', description: 'Loaded', price: 10 },
    { id: 'm3', name: 'Boba Tea', description: 'Brown sugar', price: 6 },
  ],
  'sb-the-tipsy-trailer': [
    { id: 'm1', name: 'Craft Margarita', description: 'Fresh lime, 21+', price: 13 },
    { id: 'm2', name: 'Paloma', description: 'Grapefruit, 21+', price: 12 },
  ],
};

export function sandboxMenu(truckId) {
  return SANDBOX_MENUS[truckId] || [];
}

// ── Mutable sandbox session state ──
// Demo data lives in module memory. The signed-in demo user alone is
// mirrored to tab-scoped sessionStorage: entering a URL directly (e.g.
// /admin/monitor) is a full reload, which would otherwise wipe the session
// and bounce guarded routes to login. Cleared on sign-out and tab close;
// reads are gated on SANDBOX_ENABLED so production builds never restore it.
const SESSION_KEY = 'vgo_sandbox_user';

const state = {
  user: null,
  bookings: [],
  favorites: new Set(),
  myTruck: null,
  events: [],
  orders: [],
  credentials: {},
  credentialDocs: [],
  applications: [],
  adminVendors: [],
  tagSeq: 0,
};

export function sandboxSignIn({ name, email, role }) {
  state.user = {
    id: sid('u'), name: name || 'Sandbox User', email: email || 'sandbox@vgo.demo',
    role: role || 'customer', sandbox: true,
  };
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(state.user)); } catch {}
  return state.user;
}

export function getSandboxUser() {
  if (!state.user && SANDBOX_ENABLED) {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) state.user = JSON.parse(raw);
    } catch {}
  }
  return state.user;
}

export function sandboxSignOut() {
  state.user = null;
  try { sessionStorage.removeItem(SESSION_KEY); } catch {}
}

// ── Admin master-dashboard demo (log in with an "admin…" identifier) ──
// Mirrors the real seeding API shapes so the dashboard can be exercised
// end-to-end with zero backend. Claim URLs are clearly fake: the tokens
// resolve nowhere, because consent is never simulated in sandbox.
const demoOrigin = () => (typeof window !== 'undefined' ? window.location.origin : 'https://vendorsgoonline.com');

export function sandboxAdminVendors() {
  return state.adminVendors;
}

export function sandboxAdminCreateVendor({ name, email, category, city, description, websiteUrl, photos }) {
  const vendor = {
    id: sid('av'),
    name: name || 'Demo Business',
    businessEmail: email || null,
    websiteUrl: websiteUrl || '',
    description: description || '',
    // v2 funnel states: link_sent when an email (and so a link) exists.
    claimStatus: email ? 'link_sent' : 'unclaimed',
    category: category || 'Various',
    city: city || 'San Diego',
    createdAt: new Date().toISOString(),
    claimExpiresAt: email ? new Date(Date.now() + 7 * 86400000).toISOString() : null,
    photoCount: (photos || []).length,
    sandbox: true,
  };
  state.adminVendors.unshift(vendor);
  return {
    ...vendor,
    // Mirrors the real API: a claim link only exists when an email does.
    claimUrl: email ? `${demoOrigin()}/claim/sandbox-demo-${vendor.id}` : null,
    needsClaimRequest: !email,
  };
}

export function sandboxAdminRegenerateLink(id) {
  const vendor = state.adminVendors.find((v) => v.id === id);
  if (!vendor) return null;
  vendor.claimExpiresAt = new Date(Date.now() + 7 * 86400000).toISOString();
  return {
    claimUrl: `${demoOrigin()}/claim/sandbox-demo-${id}-${++state.tagSeq}`,
    claimExpiresAt: vendor.claimExpiresAt,
  };
}

export function sandboxAdminRemoveVendor(id) {
  state.adminVendors = state.adminVendors.filter((v) => v.id !== id);
  return { removed: true };
}

export function sandboxCreateBooking({ truckId, event, hours }) {
  const vendor = SANDBOX_VENDORS.find((v) => v.id === truckId) || SANDBOX_VENDORS[0];
  const subtotal = vendor.priceUnit === 'guest'
    ? vendor.priceFrom * (event?.guestCount || 25)
    : vendor.priceFrom;
  const total = Math.round(subtotal * 1.1 * 100) / 100;
  const booking = {
    _id: sid('b'),
    truck: { _id: vendor.id, name: vendor.name },
    customer: { name: state.user?.name || 'Sandbox User' },
    vendor: { name: vendor.name },
    event, hours: hours || 4, status: 'pending', paymentStatus: 'deposit_pending',
    pricing: { totalAmount: total, totalAfterDiscount: total, depositAmount: Math.round(total * 25) / 100, remainingAmount: Math.round(total * 75) / 100 },
    createdAt: new Date().toISOString(),
    sandbox: true,
  };
  state.bookings.push(booking);
  return booking;
}

export function sandboxBookings() {
  return state.bookings;
}

export function sandboxToggleFavorite(truckId) {
  if (state.favorites.has(truckId)) {
    state.favorites.delete(truckId);
    return { saved: false };
  }
  state.favorites.add(truckId);
  return { saved: true };
}

export function sandboxFavorites() {
  return SANDBOX_VENDORS.filter((v) => state.favorites.has(v.id));
}

export function sandboxUpsertTruck(fields) {
  state.myTruck = { ...(state.myTruck || { _id: sid('t'), stats: {}, sandbox: true }), ...fields };
  return state.myTruck;
}

export function sandboxMyTruck() {
  return state.myTruck;
}

// ── Credential vault ──
// The real vault encrypts permit numbers and the EIN; the sandbox goes
// further and never holds them at all, in memory or otherwise. It records
// only the same `onFile` flag the API returns after masking, so the UI sees
// the identical shape without a secret ever existing in demo mode.
const SECRET_LEAVES = [
  ['legal', 'sellersPermit', 'number'],
  ['legal', 'businessLicense', 'number'],
  ['legal', 'ein', 'value'],
];

function mergeDeep(base, patch) {
  const out = { ...base };
  for (const [key, value] of Object.entries(patch || {})) {
    out[key] = value && typeof value === 'object' && !Array.isArray(value)
      ? mergeDeep(out[key] || {}, value)
      : value;
  }
  return out;
}

export function sandboxCredentials() {
  return { vault: state.credentials, docs: state.credentialDocs };
}

export function sandboxSaveCredentials(patch) {
  const merged = mergeDeep(state.credentials, patch);

  for (const [section, group, leaf] of SECRET_LEAVES) {
    const target = merged[section]?.[group];
    if (!target || !(leaf in target)) continue;
    const entered = String(target[leaf] ?? '').trim();
    if (leaf === 'value') target.last4 = entered ? entered.replace(/\D/g, '').slice(-4) : null;
    target.onFile = entered.length > 0;
    delete target[leaf];
  }

  state.credentials = merged;
  return sandboxCredentials();
}

export function sandboxUploadCredentialDoc(file, path) {
  const doc = {
    _id: sid('cd'), kind: path, mime: file.type, size: file.size,
    originalName: file.name, uploadedAt: new Date().toISOString(), sandbox: true,
    // Same trick as the sandbox image upload: the file never leaves the tab.
    viewUrl: URL.createObjectURL(file),
  };
  state.credentialDocs = [doc, ...state.credentialDocs.filter((d) => d.kind !== path)];

  const [section, group] = path.split('.');
  state.credentials = mergeDeep(state.credentials, { [section]: { [group]: { docId: doc._id } } });
  return sandboxCredentials();
}

export function sandboxDeleteCredentialDoc(docId) {
  const doc = state.credentialDocs.find((d) => d._id === docId);
  state.credentialDocs = state.credentialDocs.filter((d) => d._id !== docId);

  if (doc) {
    const [section, group] = doc.kind.split('.');
    state.credentials = mergeDeep(state.credentials, { [section]: { [group]: { docId: null } } });
  }
  return sandboxCredentials();
}

export function sandboxConversations() {
  const v = SANDBOX_VENDORS[0];
  return [{
    _id: 'sb-conversation-taco-norte',
    participants: [
      { _id: state.user?.id || 'me', name: state.user?.name || 'You', role: 'customer' },
      { _id: 'sb-user-taco-norte', name: v.name, role: 'vendor' },
    ],
    truck: { name: v.name },
    lastMessage: { content: 'Hi! This is a sandbox conversation - messaging goes live with the real backend.', createdAt: new Date().toISOString() },
    updatedAt: new Date().toISOString(),
    sandbox: true,
  }];
}

export function sandboxMessages(conversationId) {
  return [{
    _id: 'sb-message-welcome',
    conversation: conversationId,
    sender: { _id: 'vendor', name: 'Taco Norte' },
    content: 'Hi! This is a sandbox conversation - messaging goes live with the real backend.',
    createdAt: new Date().toISOString(),
  }];
}

export function sandboxSendMessage(conversationId, content) {
  return {
    _id: sid('m'),
    conversation: conversationId,
    sender: { _id: state.user?.id || 'me', name: state.user?.name || 'You' },
    content,
    createdAt: new Date().toISOString(),
  };
}

// ── Preorder: events + orders (memory only) ───────────────────
const DEMO_LINEUP = ['sb-taco-norte', 'sb-seoul-wheels', 'sb-the-tipsy-trailer'];

function ensureDemoEvent() {
  if (state.events.length) return;
  const today = new Date();
  today.setDate(today.getDate() + 1);
  state.events.push({
    id: 'sb-event-demo',
    name: 'Waterfront Food Fest',
    slug: 'waterfront-food-fest-demo',
    date: today.toISOString(),
    startTime: '11:00', endTime: '21:00',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
    location: { address: 'Embarcadero', city: 'San Diego', state: 'CA' },
    isLive: true,
    trucks: DEMO_LINEUP.map((id) => ({ truck: id })),
    sandbox: true,
  });
  // Second demo event exercises Phase 4-7 end to end offline: requirements
  // readiness, instant accept via auto-approved application, and the
  // booth-fee request → pay flow in My Applications.
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 10);
  state.events.push({
    id: 'sb-event-market',
    name: 'Harborside Night Market',
    slug: 'harborside-night-market-demo',
    date: nextWeek.toISOString(),
    startTime: '16:00', endTime: '22:00',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
    location: { address: 'Harbor Dr', city: 'San Diego', state: 'CA' },
    isLive: true,
    status: 'open',
    applicationMode: 'open',
    categoriesNeeded: ['vendor', 'catering'],
    guestCount: 400,
    requirements: [
      { key: 'sellers_permit', required: true },
      { key: 'coi', required: true, minCoverage: 1000000 },
      { key: 'liability_waiver', required: true },
      { key: 'booth_fee', required: true, feeAmountCents: 15000 },
    ],
    trucks: [],
    sandbox: true,
  });
}

function truckQueueSnapshot(truckId) {
  const active = state.orders.filter((o) => o.truck._id === truckId && ['placed', 'accepted', 'preparing', 'ready'].includes(o.status));
  const openCurrent = active.filter((o) => ['accepted', 'preparing'].includes(o.status)).length;
  const estWaitMin = openCurrent * 12;
  return {
    truckId, open: active.length, openCurrent, avgPrepMinutes: 12, estWaitMin,
    estWaitBand: estWaitMin === 0 ? 'No wait' : `~${Math.max(5, Math.round(estWaitMin * 0.75))}–${Math.round(estWaitMin * 1.25)} min`,
  };
}

// Mirror of backend config/requirementTemplates.js so the host builder works
// offline. `defaults` prefill the per-item option inputs.
const REQUIREMENT_TEMPLATES = [
  { key: 'sellers_permit', label: "Seller's permit", category: 'legal', type: 'credential' },
  { key: 'business_license', label: 'Business license', category: 'legal', type: 'credential' },
  { key: 'ein_w9', label: 'EIN / W-9', category: 'legal', type: 'credential' },
  { key: 'dba', label: 'DBA / fictitious business name', category: 'legal', type: 'credential' },
  { key: 'coi', label: 'Certificate of insurance (COI)', category: 'insurance', type: 'credential', options: ['minCoverage'], defaults: { minCoverage: 1000000 } },
  { key: 'additionally_insured', label: 'Additionally insured endorsement', category: 'insurance', type: 'credential', defaults: { needsAdditionallyInsured: true } },
  { key: 'liability_waiver', label: 'Liability waiver', category: 'insurance', type: 'acknowledgment', options: ['ackText'], defaults: { ackText: 'I accept full responsibility for my equipment and operations at this event and release the organizer from liability for loss, damage or injury arising from my participation.' } },
  { key: 'tff_permit', label: 'Temporary food facility (TFF) permit', category: 'foodSafety', type: 'credential' },
  { key: 'cfo_license', label: 'Cottage food (CFO) license', category: 'foodSafety', type: 'credential' },
  { key: 'food_handler', label: 'Food handler card', category: 'foodSafety', type: 'credential' },
  { key: 'commissary', label: 'Commissary agreement', category: 'foodSafety', type: 'credential' },
  { key: 'product_photos', label: 'Product photos', category: 'curation', type: 'photo' },
  { key: 'booth_photo', label: 'Booth photo', category: 'curation', type: 'photo' },
  { key: 'social_links', label: 'Social media links', category: 'curation', type: 'credential' },
  { key: 'materials_breakdown', label: 'Materials / sourcing breakdown', category: 'curation', type: 'field' },
  { key: 'booth_fee', label: 'Booth fee', category: 'logistics', type: 'payment', options: ['feeAmountCents'] },
  { key: 'equipment_compliance', label: 'Equipment compliance', category: 'logistics', type: 'acknowledgment', options: ['ackText'], defaults: { ackText: "My canopy, tables and equipment meet the event's safety rules, including fire-retardant certification and weighted canopy legs." } },
  { key: 'power_disclosure', label: 'Power requirements disclosure', category: 'logistics', type: 'credential' },
  { key: 'loadin_ack', label: 'Load-in / load-out schedule', category: 'logistics', type: 'acknowledgment', options: ['ackText'], defaults: { ackText: "I will follow the event's load-in and load-out schedule and understand that late arrival may forfeit my space." } },
];

export function sandboxRequirementTemplates() {
  return REQUIREMENT_TEMPLATES;
}

// Mirror of backend config/agreementTemplates.js (text included so the
// signing panel renders offline) and config/attestation.js.
const AGREEMENT_TEMPLATES = [
  {
    key: 'standard-vendor-agreement-v1',
    label: 'VGO standard vendor agreement',
    text: 'VENDOR AGREEMENT\n\n1. Participation. The vendor agrees to participate in the event on the date, time and location published by the event organizer, and to occupy only the space assigned.\n\n2. Conduct and compliance. The vendor will comply with all applicable laws, permits and license requirements, with the event\'s posted rules, and with reasonable instructions from the organizer during the event.\n\n3. Equipment and safety. The vendor is responsible for their own equipment, signage and setup, and confirms that all equipment meets applicable safety requirements.\n\n4. Liability. The vendor accepts full responsibility for their operations at the event and releases the organizer from liability for loss, damage or injury arising from the vendor\'s participation, except where caused by the organizer\'s negligence.\n\n5. Cancellation. If the vendor withdraws, or is removed for a breach of this agreement, any booth fee is handled under the event\'s published refund policy.',
  },
];

const ATTESTATION = {
  version: 'v1',
  text: 'ACCURACY OF INFORMATION\nBy signing, I confirm that all information I have provided in this application and on my VGO vendor profile, including licenses, permits, insurance, certifications, photos, and business details, is true, complete, and current as of the signing date. I understand that the event organizer is relying on this information, that providing false or expired information may result in removal from the event without refund, and that I am responsible for updating my profile if any of this information changes before the event.',
};

export function sandboxAgreementTemplates() {
  return AGREEMENT_TEMPLATES;
}

export function sandboxUploadAgreementDoc(file) {
  return { docId: sid('doc'), contentHash: 'sandbox', originalName: file?.name || 'agreement.pdf', mime: file?.type || 'application/pdf' };
}

export function sandboxCreateEvent(payload) {
  const ev = {
    id: sid('ev'),
    name: payload.name || 'My Event',
    slug: `${String(payload.name || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${sid('x').slice(-4)}`,
    date: payload.date || new Date().toISOString(),
    startTime: payload.startTime, endTime: payload.endTime,
    timeZone: payload.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
    location: payload.location || {},
    isLive: false,
    status: 'draft',
    categoriesNeeded: payload.categoriesNeeded || [],
    guestCount: payload.guestCount ?? null,
    applicationMode: payload.applicationMode || 'open',
    requirements: payload.requirements || [],
    applicationDeadline: payload.applicationDeadline || null,
    trucks: (payload.truckBookingIds || []).map(() => ({ truck: DEMO_LINEUP[0] })),
    sandbox: true,
  };
  state.events.push(ev);
  return ev;
}

export function sandboxMyEvents() {
  ensureDemoEvent();
  return state.events;
}

export function sandboxEvent(slug) {
  ensureDemoEvent();
  const ev = state.events.find((e) => e.slug === slug) || state.events[0];
  const trucks = (ev.trucks || []).map((t) => {
    const id = typeof t.truck === 'string' ? t.truck : t.truck?.id;
    const v = SANDBOX_VENDORS.find((x) => x.id === id) || SANDBOX_VENDORS[0];
    return {
      id: v.id, name: v.name, cuisine: v.tagline, coverPhoto: v.coverPhoto,
      providerType: v.providerType, rating: v.rating,
      menuItems: sandboxMenu(v.id),
      acceptingOrders: sandboxMenu(v.id).length > 0,
      prepTime: 12, queue: truckQueueSnapshot(v.id),
    };
  });
  return {
    id: ev.id, name: ev.name, slug: ev.slug, date: ev.date,
    startTime: ev.startTime, endTime: ev.endTime, timeZone: ev.timeZone, location: ev.location,
    coverPhoto: null, isLive: ev.isLive, orderingEnabled: true, trucks,
  };
}

export function sandboxPublishedEvents() {
  ensureDemoEvent();
  return state.events.filter((e) => e.isLive).map((e) => ({
    id: e.id, name: e.name, slug: e.slug, date: e.date,
    startTime: e.startTime, endTime: e.endTime, timeZone: e.timeZone, location: e.location,
    coverPhoto: null,
    vendorCount: (e.trucks || []).length,
    vendors: (e.trucks || []).slice(0, 4).map((t) => {
      const id = typeof t.truck === 'string' ? t.truck : t.truck?.id;
      const v = SANDBOX_VENDORS.find((x) => x.id === id);
      return { id, name: v?.name, coverPhoto: null };
    }),
  }));
}

export function sandboxEventVendorProfile(slug, truckId) {
  const ev = sandboxEvent(slug);
  const v = SANDBOX_VENDORS.find((x) => x.id === truckId) || SANDBOX_VENDORS[0];
  return {
    event: { id: ev.id, name: ev.name, slug: ev.slug, date: ev.date, timeZone: ev.timeZone },
    profile: {
      id: v.id, name: v.name, description: v.description, cuisine: v.tagline,
      cuisineType: [], coverPhoto: null, photos: [],
      rating: v.rating, reviewsCount: v.reviewsCount,
      menuItems: sandboxMenu(v.id),
      acceptingOrders: sandboxMenu(v.id).length > 0,
    },
  };
}

export function sandboxPublishEvent(eventId, isLive) {
  const ev = state.events.find((e) => e.id === eventId);
  if (ev) {
    ev.isLive = isLive;
    if (ev.status !== 'confirmed') ev.status = isLive ? 'open' : 'draft';
  }
  return { isLive, status: ev?.status, slug: ev?.slug };
}

// The demo vendor holds most credentials but not the food-safety permits, so
// the readiness UI shows both satisfied and missing states.
const DEMO_HELD_CREDENTIALS = new Set([
  'sellers_permit', 'business_license', 'ein_w9', 'coi', 'additionally_insured',
  'food_handler', 'commissary', 'social_links', 'power_disclosure',
]);

function sandboxMatch(requirements) {
  const satisfied = [];
  const missing = [];
  for (const r of requirements || []) {
    const t = REQUIREMENT_TEMPLATES.find((x) => x.key === r.key);
    if (!t || t.type !== 'credential') continue;
    (DEMO_HELD_CREDENTIALS.has(r.key) ? satisfied : missing).push(r.key);
  }
  const evaluable = satisfied.length + missing.length;
  return {
    eligible: missing.length === 0,
    score: evaluable === 0 ? 100 : Math.round((satisfied.length / evaluable) * 100),
    satisfied, missing, expiringConflicts: [],
    actions: missing.map((k) => `upload_${k}`),
  };
}

export function sandboxOpenEvents(readyOnly = false) {
  ensureDemoEvent();
  const list = state.events
    .filter((e) => e.isLive && (e.status || 'open') === 'open')
    .map((e) => ({
      id: e.id, name: e.name, date: e.date, startTime: e.startTime, endTime: e.endTime,
      timeZone: e.timeZone, location: { city: e.location?.city, state: e.location?.state },
      guestCount: e.guestCount ?? null,
      categoriesNeeded: e.categoriesNeeded?.length ? e.categoriesNeeded : ['vendor'],
      applicationMode: e.applicationMode || 'open',
      acceptedCount: (e.trucks || []).length, coverPhoto: null,
      match: sandboxMatch(e.requirements),
    }));
  return readyOnly ? list.filter((e) => e.match.eligible) : list;
}

export function sandboxEventReadiness(eventId) {
  const ev = state.events.find((e) => e.id === eventId);
  const requirements = (ev?.requirements || []).map((r) => {
    if (r.agreement) {
      return {
        key: r.key, label: r.label || 'Agreement', category: 'legal', type: 'signature',
        required: r.required !== false,
        agreement: { ...r.agreement, version: r.agreement.version || 'v1' },
      };
    }
    const t = REQUIREMENT_TEMPLATES.find((x) => x.key === r.key);
    return {
      key: r.key,
      label: t?.label || r.customPrompt || r.key,
      category: t?.category || 'custom',
      type: t?.type || 'field',
      required: r.required !== false,
      minCoverage: r.minCoverage,
      customPrompt: r.customPrompt,
      ackText: r.ackText || t?.defaults?.ackText,
      feeAmountCents: r.feeAmountCents,
    };
  });
  return {
    eventId,
    applicationMode: ev?.applicationMode || 'open',
    requirements,
    match: sandboxMatch(ev?.requirements),
  };
}

export function sandboxStartApplication(eventId) {
  const existing = state.applications.find(
    (a) => a.event?.id === eventId && !['withdrawn', 'rejected'].includes(a.status));
  if (existing) return existing;
  const ev = state.events.find((e) => e.id === eventId);
  const app = {
    _id: sid('app'),
    event: ev ? { id: ev.id, name: ev.name, slug: ev.slug, date: ev.date, applicationMode: ev.applicationMode || 'open' } : { id: eventId },
    status: 'draft',
    responses: [],
    createdAt: new Date().toISOString(),
    sandbox: true,
  };
  state.applications.push(app);
  return app;
}

export function sandboxSaveApplication(applicationId, responses) {
  const app = state.applications.find((a) => a._id === applicationId);
  if (app && app.status === 'draft') app.responses = responses || [];
  return app;
}

export function sandboxGetAgreement(applicationId, requirementKey) {
  const app = state.applications.find((a) => a._id === applicationId);
  const ev = state.events.find((e) => e.id === app?.event?.id);
  const r = (ev?.requirements || []).find((x) => x.key === requirementKey);
  const template = AGREEMENT_TEMPLATES.find((t) => t.key === r?.agreement?.templateKey);
  return {
    key: requirementKey,
    label: r?.label || 'Agreement',
    version: r?.agreement?.version || 'v1',
    contentHash: r?.agreement?.contentHash || 'sandbox',
    attestation: ATTESTATION,
    text: template?.text || 'Sandbox demo agreement - the host\'s uploaded document appears here when a backend is connected.',
  };
}

export function sandboxSignAgreement(applicationId, { requirementKey, typedName, signingDate }) {
  const app = state.applications.find((a) => a._id === applicationId);
  const ev = state.events.find((e) => e.id === app?.event?.id);
  const r = (ev?.requirements || []).find((x) => x.key === requirementKey);
  const record = {
    id: sid('sig'),
    requirementKey,
    agreement: { source: r?.agreement?.source || 'template', templateKey: r?.agreement?.templateKey, version: r?.agreement?.version || 'v1' },
    attestationVersion: ATTESTATION.version,
    typedName,
    signingDate,
    signedAt: new Date().toISOString(),
    status: 'signed',
    hasPdf: false,
  };
  if (app) {
    app.signatures = (app.signatures || []).map((s) => (
      s.requirementKey === requirementKey ? { ...s, status: 'superseded' } : s));
    app.signatures.push(record);
  }
  return { signatureRecordId: record.id, signedAt: record.signedAt, version: record.agreement.version, downloadUrl: null };
}

export function sandboxApplicationSignatures(applicationId) {
  const app = state.applications.find((a) => a._id === applicationId);
  return [...(app?.signatures || [])].reverse();
}

export function sandboxSubmitApplication(applicationId) {
  const app = state.applications.find((a) => a._id === applicationId);
  if (!app) return { status: 'submitted' };
  const open = (app.event?.applicationMode || 'open') === 'open';
  app.status = open ? 'approved' : 'submitted';
  app.submittedAt = new Date().toISOString();
  // Mirror the backend: approval is the booth-fee charge trigger.
  const ev = state.events.find((e) => e.id === app.event?.id);
  const feeReq = (ev?.requirements || []).find((r) => r.key === 'booth_fee');
  let boothFeeDue = null;
  if (open && feeReq) {
    const dueAt = new Date(Date.now() + 7 * 864e5).toISOString();
    app.boothFee = { required: true, amountCents: feeReq.feeAmountCents || 15000, status: 'pending', dueAt };
    boothFeeDue = { amountCents: app.boothFee.amountCents, dueAt };
  }
  if (open) sandboxAcceptOpenEvent(app.event?.id);
  return { status: app.status, submittedAt: app.submittedAt, boothFeeDue };
}

export function sandboxPayBoothFee(applicationId) {
  const app = state.applications.find((a) => a._id === applicationId);
  const amount = (app?.boothFee?.amountCents || 15000) / 100;
  // Sandbox payments always succeed - settle now so a reload stays paid.
  if (app?.boothFee) app.boothFee.status = 'paid';
  return { clientSecret: 'sandbox', sandbox: true, amount };
}

export function sandboxWithdrawApplication(applicationId) {
  const app = state.applications.find((a) => a._id === applicationId);
  if (app) app.status = 'withdrawn';
  return { withdrawn: true };
}

export function sandboxMyApplications() {
  return [...state.applications].reverse();
}

// Host demo: a juried event shows one submitted demo application so the
// inbox and review flow are visible offline.
export function sandboxEventApplications(eventId) {
  const ev = state.events.find((e) => e.id === eventId);
  const mode = ev?.applicationMode || 'open';
  if (!ev || mode !== 'application') return { applications: [], capacity: [], applicationMode: mode };
  const v = SANDBOX_VENDORS[0];
  const m = sandboxMatch(ev.requirements);
  return {
    applicationMode: 'application',
    capacity: [],
    applications: [{
      id: 'app-demo-1',
      status: 'submitted',
      submittedAt: new Date().toISOString(),
      boothFee: null,
      vendor: { id: v.id, name: v.name, providerType: v.providerType || 'vendor', cuisine: v.tagline, coverPhoto: v.coverPhoto },
      match: { eligible: m.eligible, score: m.score, missingCount: m.missing.length, expiringCount: 0 },
      unsignedAgreements: 0,
    }],
  };
}

export function sandboxReviewApplication() {
  const ev = state.events.find((e) => (e.applicationMode || 'open') === 'application');
  const v = SANDBOX_VENDORS[0];
  const items = (ev?.requirements || []).map((r) => {
    if (r.agreement) {
      return { key: r.key, label: r.label || 'Agreement', type: 'signature', satisfiedBy: 'signature' };
    }
    const t = REQUIREMENT_TEMPLATES.find((x) => x.key === r.key);
    const type = t?.type || 'field';
    const base = { key: r.key, label: t?.label || r.customPrompt || r.key, type };
    if (type === 'credential' || type === 'photo') {
      return { ...base, satisfiedBy: 'vault', verifiedValidAtEventDate: true, details: { note: 'Sandbox demo - on file' } };
    }
    if (type === 'acknowledgment') {
      return { ...base, satisfiedBy: 'ack', acknowledged: true, ackAt: new Date().toISOString(), ackText: r.ackText || t?.defaults?.ackText };
    }
    if (type === 'payment') return { ...base, satisfiedBy: 'payment' };
    return { ...base, satisfiedBy: 'manual', answer: 'Sandbox demo answer.' };
  });
  return {
    id: 'app-demo-1',
    status: 'under_review',
    submittedAt: new Date().toISOString(),
    hostViewedAt: new Date().toISOString(),
    boothFee: null,
    vendor: { id: v.id, name: v.name, providerType: v.providerType || 'vendor', cuisine: v.tagline, coverPhoto: v.coverPhoto },
    items,
  };
}

export function sandboxDecideApplication(applicationId, decision) {
  return { status: decision };
}

export function sandboxAcceptOpenEvent(eventId) {
  const ev = state.events.find((e) => e.id === eventId);
  if (ev) ev.trucks = [...(ev.trucks || []), { truck: DEMO_LINEUP[0] }];
  return { accepted: true, acceptedCount: ev ? ev.trucks.length : 1 };
}

export function sandboxConfirmEvent(eventId) {
  const ev = state.events.find((e) => e.id === eventId);
  if (ev) ev.status = 'confirmed';
  return { status: 'confirmed', slug: ev?.slug, acceptedCount: (ev?.trucks || []).length };
}

export function sandboxCancelEvent(eventId) {
  const ev = state.events.find((e) => e.id === eventId);
  if (ev) { ev.status = 'cancelled'; ev.isLive = false; }
  return { status: 'cancelled' };
}

export function sandboxPlaceOrder(payload) {
  const v = SANDBOX_VENDORS.find((x) => x.id === payload.truckId) || SANDBOX_VENDORS[0];
  state.tagSeq += 1;
  const items = (payload.items || []).map((i) => ({ ...i, itemTotal: Math.round(i.price * i.quantity * 100) / 100 }));
  const subtotal = items.reduce((s, i) => s + i.itemTotal, 0);
  const order = {
    _id: sid('ord'),
    tag: `VGO-${String(state.tagSeq).padStart(3, '0')}`,
    truck: { _id: v.id, name: v.name },
    vendor: { name: v.name },
    customer: { name: state.user?.name || 'Guest' },
    customerName: state.user?.name || 'Guest',
    event: payload.eventId || null,
    items,
    pickupAt: payload.pickupAt || null,
    pricing: { subtotal, serviceFee: 0, totalAmount: subtotal }, // no fee
    status: 'placed', paymentStatus: 'paid',
    createdAt: new Date().toISOString(),
    sandbox: true,
  };
  state.orders.push(order);
  return order;
}

export function sandboxMyOrders() {
  return state.orders.filter((o) => o.customer.name === (state.user?.name || 'Guest'));
}

export function sandboxGetOrder(id) {
  return state.orders.find((o) => o._id === id) || null;
}

export function sandboxVendorIncoming() {
  return state.orders.filter((o) => ['placed', 'accepted', 'preparing', 'ready'].includes(o.status));
}

export function sandboxVendorQueue() {
  return sandboxVendorIncoming();
}

export function sandboxSetOrderStatus(id, status) {
  const o = state.orders.find((x) => x._id === id);
  if (o) {
    o.status = status;
    if (status === 'ready') o.readyAt = new Date().toISOString();
  }
  return { message: `Order ${status}`, order: o };
}
